import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui";
import t from "./theme";
import { number, usdValue, groupKey, rollUpPositions, venueOnRow } from "./positionRollup";
import { ExportCsvButton, InfoTip } from "./PageTools";
import { dealReportPath } from "./links";
import { day } from "./format";
import "./DashboardPositions.css";

const format = (value, digits = 2) => {
  const n = number(value);
  if (n === null) return "—";
  const text = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return n < 0 ? `(${text})` : text;
};
const sum = rows => rows.every(p => usdValue(p) !== null) ? rows.reduce((total, p) => total + usdValue(p), 0) : null;
// The unrealised figure the row shows: the engine's when it has one, and the
// difference otherwise. The column has to sort on what is on screen, not on a
// field the screen only sometimes uses.
const pnlOf = p => {
  const local = number(p.market_value), cost = number(p.cost_basis);
  return number(p.unrealized_pnl) ?? (local !== null && cost !== null ? local - cost : null);
};
const missing = v => v === null || v === undefined || v === "";
// A missing figure is not a small one, so it sorts to the bottom in **both**
// directions. Ordering a "—" as zero would put an unpriced position among the
// ones worth nothing, which is a claim the dash never made (§0.17).
const compareBy = (column, dir) => (a, b) => {
  const av = column.get(a), bv = column.get(b);
  if (missing(av) || missing(bv)) return missing(av) && missing(bv) ? 0 : missing(av) ? 1 : -1;
  const d = column.numeric ? av - bv : String(av).localeCompare(String(bv), "en");
  return dir === "asc" ? d : -d;
};
const symbolKey = value => String(value || "").toUpperCase().replace(/\s+[A-Z]{2}\s+EQUITY$/, "").replace(/[\s.-]/g, "");
const brokerName = p => p.broker || p.source || (p.instrument_type === "Equity (CS)" ? "Clear Street" : "Prime broker");
// Is this broker row the same instrument as this position?
//
// **The identifiers decide, and the symbol is only the fallback.** §0.9: search
// broker rows by security or cusip, never by symbol text — a ticker changes on
// a delisting or a rename and the row keeps whatever the broker printed that
// day. Clear Street reports Valuno Group AB (publ)'s ordinary shares under the
// CUSIP "W7S95L109" and no symbol at all, so no spelling of the symbol could
// ever have matched it, and 17,983,757 shares read as a broker holding of
// something nobody could name.
//
// A CUSIP or an ISIN identifies an instrument outright, so a match on one
// answers the question on its own: the instrument type and the currency are not
// consulted, because a broker reporting a Swedish line in dollars while the
// book carries it in krona is a fact about two reports of one instrument and
// not a reason to call them different things. The name and symbol fallback
// keeps both tests, because on that path they are what stops a false match.
const idKey = value => String(value || "").trim().toUpperCase();
const sameIdentifier = (a, b) => (idKey(a.cusip) && idKey(a.cusip) === idKey(b.cusip))
  || (idKey(a.isin) && idKey(a.isin) === idKey(b.isin))
  // Clear Street puts the CUSIP in the symbol column when it has no symbol,
  // which is the case in front of us: the position's CUSIP matches the text
  // the broker printed as a name.
  || (idKey(a.cusip) && idKey(a.cusip) === idKey(b.ticker))
  || (idKey(b.cusip) && idKey(b.cusip) === idKey(a.ticker));
const sameSecurity = (position, broker) => sameIdentifier(position, broker) || (
  position.instrument_type === "Equity" && position.currency === broker.currency && (
    (symbolKey(position.ticker) && symbolKey(position.ticker) === symbolKey(broker.ticker)) ||
    (position.name && broker.name && position.name.trim().toLowerCase() === broker.name.trim().toLowerCase())
  )
);

/* ── What the table is built from ─────────────────────────────────────── */

/**
 * The NAV payload as the reader's positions: tax lots rolled up per
 * instrument and venue, another entity's holdings apart, and the cash
 * accounts. The Dashboard's figures row reads the same model, so the exposure
 * it prints is the sum of the rows this table shows (§0.21).
 */
export function positionModel(data) {
  const lots = (data?.positions || []).filter(p => !p.ticker?.startsWith("TEST"));
  const brokerPositions = (data?.broker_positions || []).filter(p => number(p.quantity) !== 0);
  // Where the position is held, and the only thing that splits one security
  // into two rows.
  //
  // A tax lot records no custodian, so `broker` on the row is what the book
  // would have to carry for Brian's exception — the same security at two prime
  // brokers — to be two rows, and nothing writes it yet. Until it does, the
  // venue is read off the broker files, which report per security and not per
  // lot: every lot of one security resolves to the same venues, so today the
  // exception shows as one row naming both. That is the honest reading of the
  // data rather than a split invented from broker-reported quantities. A venue
  // that files nothing here at all (Cantor, ITG — §0.18) cannot be named.
  const venueOf = p => venueOnRow(p)
    || [...new Set(brokerPositions.filter(b => sameSecurity(p, b)).map(brokerName))].join(", ");
  const positions = rollUpPositions(lots, venueOf);
  // Positions another Helena entity holds. Brian, 20 October 2026, on Paradox
  // Fund SPC's two portfolios, which are Helena Partners Inc's deals:
  // *"Paradox should only count in HPIs personal NAV as they are the only
  // investors but we should still track the positions."* The engine leaves
  // them out of every fund figure and returns them under their owner, so they
  // are listed here and in no total.
  const outside = rollUpPositions((data?.outside_fund_positions || []).filter(p => !p.ticker?.startsWith("TEST")), venueOf)
    .map(p => ({ ...p, outsideFund: true }));
  const owners = [...new Set(outside.map(p => p.entity_name || "another Helena entity"))];
  const cash = (data?.cash || []).map(c => ({
    ...c, isCash: true, ticker: c.currency, name: "Cash", instrument_type: "Cash",
    quantity: c.balance, market_value: c.balance, mv_usd: c.balance_usd,
    last_price: 1, valuation_method: "Cash balance", lot_count: 1,
  }));
  const long = positions.filter(p => (number(p.quantity) ?? usdValue(p) ?? 0) >= 0);
  const short = positions.filter(p => (number(p.quantity) ?? usdValue(p) ?? 0) < 0);
  return { lots, positions, outside, owners, cash, long, short };
}

/**
 * Long, short, net and gross exposure, in dollars, over the fund's own
 * positions. Null where a row has no dollar figure: a total over four rows of
 * five is not the exposure (§0.30).
 */
export function exposureSummary(data) {
  const { long, short } = positionModel(data);
  const l = sum(long), s = sum(short);
  return {
    long: l,
    short: s,
    net: l !== null && s !== null ? l + s : null,
    gross: l !== null && s !== null ? Math.abs(l) + Math.abs(s) : null,
    cash: number(data?.summary?.total_cash),
    nav: number(data?.summary?.nav),
  };
}

/* ── The reader's settings, remembered per browser ────────────────────── */

const STORE = {
  columns: "pms.dashboard.hiddenColumns",
  groupBy: "pms.dashboard.groupBy",
  collapsed: "pms.dashboard.collapsed",
};
const recall = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const remember = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* a convenience only */ }
};
function useRemembered(key, fallback) {
  const [value, setValue] = useState(() => recall(key, fallback));
  useEffect(() => remember(key, value), [key, value]);
  return [value, setValue];
}

/* ── Grouping ─────────────────────────────────────────────────────────── */

// Usability review of 23 October 2026, item 12: *"Add a "group by" control
// for issuer, instrument type, currency or strategy. The current table splits
// only by long, private and short."* Long / Short stays the default, with the
// privates in their own section after Short; the others regroup the fund's own
// rows.
// Another entity's holdings and the cash stay where they always are, below.
export const GROUP_BY = [
  { key: "side", label: "Long / short" },
  { key: "issuer", label: "Issuer" },
  { key: "type", label: "Instrument type" },
  { key: "currency", label: "Currency" },
  { key: "strategy", label: "Strategy" },
];
const groupValue = (p, by) => {
  if (by === "issuer") return (p.issuer || "").trim() || "Issuer not recorded";
  if (by === "type") return p.instrument_type || "Type not recorded";
  if (by === "currency") return p.currency || "Currency not recorded";
  if (by === "strategy") return (p.strategy || "").trim() || "No strategy tag";
  return null;
};

export default function DashboardPositions({ data, initialQuery = "" }) {
  const [query, setQuery] = useState(initialQuery);
  useEffect(() => setQuery(initialQuery), [initialQuery]);
  // Which sections are folded, remembered (item 11: "Let each section ...
  // collapse and remember that setting").
  const [collapsed, setCollapsed] = useRemembered(STORE.collapsed, {});
  const [groupBy, setGroupBy] = useRemembered(STORE.groupBy, "side");
  const [hidden, setHidden] = useRemembered(STORE.columns, []);
  const [chooser, setChooser] = useState(false);
  const chooserRef = useRef(null);
  useEffect(() => {
    if (!chooser) return undefined;
    const close = e => chooserRef.current && !chooserRef.current.contains(e.target) && setChooser(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [chooser]);
  // Mkt Val (USD), largest first — the order this table has always opened in,
  // now a state a reader can change rather than one baked into the render.
  const [sort, setSort] = useState({ label: "Mkt Val (USD)", dir: "desc" });
  const [showNil, setShowNil] = useState(false);
  const model = useMemo(() => positionModel(data), [data]);
  const { lots, positions, outside, owners, cash } = model;
  // A bank account holding nothing is a fact about the account, not a holding
  // of the fund. Brian, 16 September 2026: *"i still see a bunch of USD line
  // items with zero balances. Can we autohide them if nothing in them? And if
  // money does go into them, they populate again?"* They populate again
  // because nothing is remembered: the rows are rebuilt from the NAV payload
  // on every load.
  //
  // **Nil, never blank.** `number` gives null for a balance that is missing or
  // unreadable, and that account stays on the table: a balance nobody could
  // read is not a balance of nothing (§0.17).
  const isNil = p => p.isCash && number(p.balance) === 0;
  // A filter is an explicit request for a row, so searching finds an empty
  // account by its name or its number whether or not they are being shown.
  const searching = query.trim() !== "";
  const shown = p => !isNil(p) || showNil || searching;
  const nilCash = cash.filter(isNil);
  const location = p => p.isCash ? [p.source, p.account_name].filter(Boolean).join(" · ") : p.venue;
  const allRows = [...positions, ...outside, ...cash.filter(shown)];
  const nav = number(data?.summary?.nav);
  // A share of the fund's NAV, for a figure that is in it. Another entity's
  // position is not, so its row shows a dash rather than a percentage of
  // something it is no part of.
  const percent = value => nav && number(value) !== null ? (value / nav < 0 ? `(${format(Math.abs(value / nav * 100))}%)` : `${format(value / nav * 100)}%`) : "—";
  // Brian, 20 October 2026: *"lets make a sub category here for privates.
  // Loft Capital, the PPLNs and Paradox constitute as Privates"*. The engine
  // says which rows those are (`is_private`); the screen does not guess it from
  // a missing price, because a listed share nobody has priced looks the same.
  //
  // Brian, 2 November 2026: *"move shorts above Privates"*. So the privates are
  // a section of their own after Short rather than a sub-category inside Long:
  // a header placed above Short cannot total rows that sit below it. Long and
  // Short count and total the listed names, Privates its own rows, and Long
  // plus Privates is the long exposure the Dashboard's figures row reads.
  const isPrivate = p => !p.isCash && p.is_private === true;
  const groups = groupBy === "side" || !GROUP_BY.some(g => g.key === groupBy)
    ? [
      { label: "Long", rows: model.long.filter(p => !isPrivate(p)) },
      { label: "Short", rows: model.short.filter(p => !isPrivate(p)) },
      { label: "Privates", rows: positions.filter(isPrivate) },
    ]
    : [...positions.reduce((m, p) => {
      const key = groupValue(p, groupBy);
      m.set(key, [...(m.get(key) || []), p]);
      return m;
    }, new Map())]
      .map(([label, rows]) => ({ label, rows, sortKey: Math.abs(sum(rows) ?? 0) }))
      .sort((a, b) => b.sortKey - a.sortKey || a.label.localeCompare(b.label));
  // Another entity's positions sit under the fund's own and above cash.
  for (const owner of owners) {
    groups.push({ label: `Held by ${owner} — not in fund NAV`, rows: outside.filter(p => (p.entity_name || "another Helena entity") === owner), outsideFund: true });
  }
  // Cash goes last, under the positions. Brian, 1 October 2026: *"cash & cash
  // equivalents should be at the bottom of all positions"*.
  groups.push({ label: "Cash & Equivalents", rows: cash.filter(shown) });
  const matches = p => [p.ticker, p.name, p.deal_name, p.issuer, p.strategy, p.currency, p.instrument_type, location(p), p.entity_name, isPrivate(p) ? "Private" : ""].some(v => String(v || "").toLowerCase().includes(query.trim().toLowerCase()));
  // Each column says what it sorts on, so the order can never be taken off a
  // field the cell beside it does not show. `id` is what the column chooser
  // remembers; the first column cannot be hidden.
  const columns = [
    { id: "position", label: "Position", get: p => p.name || p.ticker, fixed: true },
    { id: "ccy", label: "CCY", get: p => p.currency },
    { id: "qty", label: "TD Qty", numeric: true, get: p => number(p.quantity) },
    { id: "price", label: "Price (Local)", numeric: true, get: p => number(p.last_price) },
    { id: "mvLocal", label: "Mkt Val (Local)", numeric: true, get: p => number(p.market_value) },
    { id: "mvUsd", label: "Mkt Val (USD)", numeric: true, get: usdValue },
    { id: "pctNav", label: "Mkt Val % of NAV", numeric: true, get: usdValue },
    { id: "venue", label: "Bank / Prime Broker", get: location },
    { id: "cost", label: "Cost Basis (Local)", numeric: true, get: p => number(p.cost_basis) },
    { id: "avgCost", label: "Avg Cost / Share (Local)", numeric: true, get: p => number(p.cost_per_share) },
    { id: "pnl", label: "Unrealized P&L (Local)", numeric: true, get: pnlOf },
    { id: "method", label: "Valuation Method", get: p => p.valuation_method },
  ];
  const visible = columns.filter(c => c.fixed || !hidden.includes(c.id));
  const DEFAULT_SORT = "Mkt Val (USD)";
  const sorted = columns.find(c => c.label === sort.label)
    || columns.find(c => c.label === DEFAULT_SORT);
  // A PPLN row is the ledger's balance and has no tax lot behind it, so it
  // is a position and not a lot.
  const lotCount = lots.filter(p => p.lot_id !== null).length;
  const title = `Fund Positions — ${positions.length} Open`
    + (lotCount !== positions.length ? ` (${lotCount} lots)` : "");
  const toggle = key => setCollapsed(s => ({ ...s, [key]: !s[key] }));
  // The group header spans the columns before Mkt Val (USD) and puts its
  // total and share of NAV under those two columns, whichever are showing.
  const groupHeader = (label, rows, key, outsideFund = false) => {
    const total = sum(rows);
    const before = visible.findIndex(c => c.id === "mvUsd");
    const lead = before === -1 ? visible.length : before;
    const tail = visible.slice(lead).filter(c => c.id !== "mvUsd" && c.id !== "pctNav").length;
    return <tr key={`${key}-header`} className="positions-group">
      <td colSpan={lead} className="positions-group-label"><button onClick={() => toggle(key)} aria-expanded={!collapsed[key]}>{collapsed[key] ? "▸" : "▾"} {label} ({rows.length})</button></td>
      {before !== -1 && <td className="numeric">{format(total)}</td>}
      {visible.some(c => c.id === "pctNav") && <td className="numeric">{outsideFund ? "—" : percent(total)}</td>}
      {tail > 0 && <td colSpan={tail} />}
    </tr>;
  };
  // The average over the row's own side, and the other side named beneath it
  // rather than averaged in (positionRollup.costPerShare).
  const costPerShareCell = p => {
    const other = p.opposite_side, side = q => (q < 0 ? "short" : "long");
    const otherText = other && `${format(Math.abs(other.quantity), Number.isInteger(other.quantity) ? 0 : 2)} ${side(other.quantity)} at ${format(other.cost_per_share, 4)}`;
    return <td key="avgCost" className="numeric" title={other
      ? `${format(p.cost_per_share, 4)} is the average over the ${side(-other.quantity)} lots only. This row also holds ${otherText}, which is not averaged in.`
      : undefined}>
      {format(p.cost_per_share, 4)}
      {other && <small className="positions-asat positions-offset">also {otherText}</small>}
    </td>;
  };
  // The price, with the date it is as at in the tooltip rather than on a line
  // of its own (item 11). A mark more than a week old still says so on the
  // row, as an amber dot: Valuno Group AB (publ)'s ordinary shares once showed
  // 0.0030 against a market near 0.09 with nothing on screen saying the figure
  // was old, and a tooltip alone would bring that back.
  const priceCell = p => {
    const hasPrice = p.last_price !== null && p.last_price !== undefined;
    const stale = hasPrice && p.price_age_days > 7;
    const dated = p.price_date ? `as at ${day(p.price_date)}` : "undated";
    const tip = p.isCash ? undefined
      : p.price_source ? `${p.price_source}, ${dated}${stale ? ` — ${p.price_age_days} days old` : ""}`
      : hasPrice ? undefined : "No price: carried at cost";
    return <td key="price" className="numeric" title={tip}>
      {stale && <span className="positions-stale-dot" role="img" aria-label={`price ${p.price_age_days} days old`}>●</span>}
      {format(p.last_price, 4)}
    </td>;
  };
  // Usability review, items 3 and 7: the instrument's own name, its ticker
  // underneath, and the name opens the deal's report.
  const nameCell = p => {
    const name = p.isCash ? "Cash" : (p.name || p.deal_name || p.ticker || "—");
    const label = <span className="positions-name">{name}</span>;
    return <th key="position" scope="row" className="positions-description" title={[name, p.ticker, p.deal_name && `Deal: ${p.deal_name}`].filter(Boolean).join("\n")}>
      {p.deal_id && !p.isCash ? <Link to={dealReportPath(p.deal_id)} className="positions-link-name">{label}</Link> : label}
      {(p.ticker && p.ticker !== name) || p.lot_count > 1
        ? <span className="positions-ticker">{p.ticker && p.ticker !== name && <span>{p.ticker}</span>}{p.lot_count > 1 && <span className="positions-lots">{p.ticker && p.ticker !== name ? " · " : ""}{p.lot_count} lots</span>}</span>
        : null}
    </th>;
  };
  const cellFor = (col, p) => {
    const local = number(p.market_value), pnl = pnlOf(p);
    switch (col.id) {
      case "position": return nameCell(p);
      case "ccy": return <td key="ccy">{p.currency || "—"}</td>;
      case "qty": return <td key="qty" className="numeric">{number(p.quantity) === null ? "—" : format(p.quantity, Number.isInteger(Number(p.quantity)) ? 0 : 2)}</td>;
      case "price": return priceCell(p);
      case "mvLocal": return <td key="mvLocal" className="numeric">{format(local)}</td>;
      case "mvUsd": return <td key="mvUsd" className="numeric positions-value">{format(usdValue(p))}</td>;
      case "pctNav": return <td key="pctNav" className="numeric">{p.outsideFund ? "—" : percent(usdValue(p))}</td>;
      case "venue": return <td key="venue" className="positions-venue">{location(p) || "—"}</td>;
      case "cost": return <td key="cost" className="numeric">{format(p.cost_basis)}</td>;
      case "avgCost": return costPerShareCell(p);
      case "pnl": return <td key="pnl" className="numeric" style={{ color: pnl < 0 ? t.danger : undefined }}>{format(pnl)}</td>;
      case "method": return <td key="method" className="positions-method" title={p.instrument_type}>{p.valuation_method || "—"}</td>;
      default: return null;
    }
  };
  const positionRow = (p, index) => <tr key={`${groupKey(p)}-${index}`}>{visible.map(col => cellFor(col, p))}</tr>;

  // The rows on screen, in screen order, for the export.
  const exportRows = [];
  const body = groups.map(group => {
    const rows = group.rows.filter(matches).sort(compareBy(sorted, sort.dir));
    if (!rows.length) return null;
    rows.forEach(p => exportRows.push({ ...p, group: group.label }));
    return <React.Fragment key={group.label}>
      {groupHeader(group.label, rows, group.label, Boolean(group.outsideFund))}
      {!collapsed[group.label] && rows.map(positionRow)}
    </React.Fragment>;
  });
  const exportColumns = [
    { label: "Group", value: p => p.group },
    { label: "Position", value: p => (p.isCash ? location(p) : p.name) },
    { label: "Ticker", value: p => p.ticker },
    { label: "Issuer", value: p => p.issuer },
    { label: "Deal", value: p => p.deal_name },
    { label: "Currency", value: p => p.currency },
    { label: "Quantity", value: p => number(p.quantity) },
    { label: "Price (local)", value: p => number(p.last_price) },
    { label: "Price date", value: p => p.price_date },
    { label: "Market value (local)", value: p => number(p.market_value) },
    { label: "Market value (USD)", value: usdValue },
    { label: "% of NAV", value: p => (p.outsideFund || !nav || usdValue(p) === null ? null : (usdValue(p) / nav * 100).toFixed(2)) },
    { label: "Bank / prime broker", value: location },
    { label: "Cost basis (local)", value: p => number(p.cost_basis) },
    { label: "Avg cost per share (local)", value: p => number(p.cost_per_share) },
    { label: "Unrealized P&L (local)", value: pnlOf },
    { label: "Valuation method", value: p => p.valuation_method },
  ];

  const toolbar = <div className="positions-toolbar">
    <input className="positions-search" type="search" aria-label="Filter fund positions" placeholder="Filter positions…" value={query} onChange={e => setQuery(e.target.value)} />
    <label className="positions-groupby">Group by
      <select aria-label="Group positions by" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
        {GROUP_BY.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
      </select>
    </label>
    <span className="positions-chooser" ref={chooserRef}>
      <button type="button" className="pt-button" aria-expanded={chooser} aria-haspopup="true" onClick={() => setChooser(v => !v)}>
        Columns{hidden.length ? ` (${columns.length - hidden.length}/${columns.length})` : ""}
      </button>
      {chooser && <div className="positions-chooser-pop" role="group" aria-label="Columns to show">
        {columns.filter(c => !c.fixed).map(c => <label key={c.id}>
          <input type="checkbox" checked={!hidden.includes(c.id)} onChange={() => setHidden(h => h.includes(c.id) ? h.filter(x => x !== c.id) : [...h, c.id])} />
          {c.label}
        </label>)}
        {hidden.length > 0 && <button type="button" className="positions-link" onClick={() => setHidden([])}>Show all columns</button>}
      </div>}
    </span>
    <ExportCsvButton filename="fund-positions" columns={exportColumns} rows={exportRows} />
  </div>;

  return <Card title={title} noPad action={toolbar}>
    <section className="dashboard-positions" style={{ "--positions-border": t.borderLight, "--positions-subtle": t.bgSubtle, "--positions-muted": t.textMuted, color: t.text, fontFamily: t.fontBody }}>
      <div className="positions-caption">
        {data?.as_of_date && <span>As of {day(data.as_of_date)} · </span>}
        One row per security and venue, tax lots combined. Click a name to open its deal.
        <InfoTip label="How to read this table" wide>
          <p>TD = trade date. Quantities are shares or units, principal for notes and PPLNs, or stated value for preferred.</p>
          <p>Long and Short list the listed names; the privates have their own section below Short. Long plus Privates is the long exposure in the figures above.</p>
          <p>Avg cost / share is cost basis ÷ quantity over the lots on the row's side, long or short. Local amounts are in the row's currency.</p>
          <p>A prime broker named on a row reports a holding in that security; its quantity may differ from the fund's records. A price more than a week old is marked with an amber dot; hover the price for its date and source.</p>
          {outside.length > 0 && <p>Positions held by {owners.join(" and ")} are listed under their owner and are in no fund figure.</p>}
        </InfoTip>
      </div>
      {nilCash.length > 0 && !searching && <div className="positions-caption">{showNil
        ? <>Showing {nilCash.length} bank account{nilCash.length === 1 ? "" : "s"} with a nil balance. <button type="button" className="positions-link" onClick={() => setShowNil(false)}>Hide them</button></>
        : <>{nilCash.length} bank account{nilCash.length === 1 ? "" : "s"} with a nil balance {nilCash.length === 1 ? "is" : "are"} hidden; {nilCash.length === 1 ? "it reappears" : "they reappear"} when money reaches {nilCash.length === 1 ? "it" : "them"}. <button type="button" className="positions-link" onClick={() => setShowNil(true)}>Show {nilCash.length === 1 ? "it" : "them"}</button></>}
      </div>}
      <div className="positions-scroll" tabIndex={0} role="region" aria-label="Fund positions table">
        <table><thead><tr>{visible.map(col => {
          const active = col.label === sorted.label;
          // A first click gives the order the column is usually read in —
          // largest first for money, A to Z for a name — and clicking the
          // column again reverses it.
          const next = active ? (sort.dir === "asc" ? "desc" : "asc") : (col.numeric ? "desc" : "asc");
          return <th key={col.id} scope="col" className={`${col.numeric ? "numeric" : ""}${col.fixed ? " positions-firstcol" : ""}`}
            aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
            <button type="button" className="positions-sort" onClick={() => setSort({ label: col.label, dir: next })}
              title={`Sort by ${col.label}, ${next === "asc" ? "smallest" : "largest"} first`}>
              {col.label}<span aria-hidden="true" className="positions-arrow">{active ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
            </button></th>;
        })}</tr></thead>
          <tbody>{body}
          {!allRows.some(matches) && <tr><td colSpan={visible.length} className="positions-empty">No matching positions.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  </Card>;
}
